import { ComponentProps, type ReactNode } from 'react';
import { MHMTypography } from '../MHM/MHMTypography';
import { Box, BoxProps, Stack, StackProps } from '@mui/material';
import { MHMButton } from '../MHM/MHMButton';

function Heading(props: ComponentProps<typeof MHMTypography>) {
  const { children, sx, ...rest } = props;
  return (
    <MHMTypography
      variant="h2"
      color="primary"
      sx={{ fontSize: '1.4rem', mb: '0.5rem', ...sx }}
      bold
      {...rest}
    >
      {children}
    </MHMTypography>
  );
}

function Label(props: ComponentProps<typeof MHMTypography>) {
  return <MHMTypography fontSize="1.1rem" medium component="span" {...props} />;
}

type EntryProps = StackProps & {
  label: ReactNode;
  labelProps?: ComponentProps<typeof MHMTypography>;
  direction?: 'row' | 'column';
};

function Entry({
  direction,
  label,
  labelProps,
  children,
  ...rest
}: EntryProps) {
  let directionProps: Partial<StackProps> = {};
  if (direction === 'column') {
    directionProps = {
      flexDirection: 'column',
      alignItems: 'flex-start',
      flexWrap: 'nowrap',
    };
  } else {
    directionProps = {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '0 0.25rem',
    };
  }
  return (
    <Stack marginBottom="0.3rem" {...directionProps} {...rest}>
      <Label {...labelProps}>{label}</Label>
      {children}
    </Stack>
  );
}

type TextEntryProps = {
  label: ReactNode;
  labelProps?: ComponentProps<typeof MHMTypography>;
  text: ReactNode;
  textProps?: ComponentProps<typeof MHMTypography>;
};

function TextEntry({ label, labelProps, text, textProps }: TextEntryProps) {
  return (
    <Entry label={label} labelProps={labelProps}>
      <MHMTypography fontSize="1.1rem" {...textProps}>
        {text}
      </MHMTypography>
    </Entry>
  );
}

function Button(props: ComponentProps<typeof MHMButton>) {
  return <MHMButton sx={{ mt: 1.25 }} {...props} />;
}

export function DeviceInfoSection(props: BoxProps) {
  return <Box component="section" {...props} />;
}

DeviceInfoSection.Heading = Heading;
DeviceInfoSection.Entry = Entry;
DeviceInfoSection.TextEntry = TextEntry;
DeviceInfoSection.Button = Button;
